import { useCallback, useEffect, useRef, useState } from "react";
import { getAllMolecules, getAllReactions } from "../utils/Api";
import { useDownload } from "./useDownload";

export const SMILES_EXPORT_ROW_CAP = 500_000;
const BATCH_SIZE = 200;

export type SmilesExportMode = "molecules" | "reactions" | "combined";

export interface SmilesExportFilters {
  document_id?: string;
  source?: "personal";
  organisation_id?: string;
}

export type SmilesExportStatus = "idle" | "loading" | "cancelling" | "error";

export interface UseSmilesExportOptions {
  mode: SmilesExportMode;
  filters?: SmilesExportFilters;
  scopeSlug?: string;
}

interface RawMolecule {
  smiles: string;
  document_name: string;
  page_number: number | null;
}

interface RawReactionMol {
  smiles: string;
}

interface RawReaction {
  reactants: RawReactionMol[];
  products: RawReactionMol[];
  conditions: string[];
  document_name: string;
  page_number: number | null;
}

interface ExportMolecule {
  smiles: string;
  document_name: string;
  page_number: number | null;
}

interface ExportReaction {
  reactants: string[];
  products: string[];
  conditions: string[];
  document_name: string;
  page_number: number | null;
}

const isAbortError = (err: unknown): boolean => {
  if (err instanceof Error && (err.name === "CanceledError" || err.name === "AbortError")) {
    return true;
  }
  if (typeof err === "object" && err !== null && "code" in err) {
    const code = (err as { code?: string }).code;
    if (code === "ERR_CANCELED") return true;
  }
  return false;
};

// Return the UTC date portion (YYYY-MM-DD) of a Date so the filename date
// matches the `exported_at` ISO timestamp (also UTC via toISOString).
const utcDate = (d: Date): string => d.toISOString().slice(0, 10);

const shapeReaction = (r: RawReaction): ExportReaction => ({
  reactants: (r.reactants || []).map((x) => x.smiles),
  products: (r.products || []).map((x) => x.smiles),
  conditions: r.conditions || [],
  document_name: r.document_name,
  page_number: r.page_number ?? null,
});

const shapeMolecule = (m: RawMolecule): ExportMolecule => ({
  smiles: m.smiles,
  document_name: m.document_name,
  page_number: m.page_number ?? null,
});

export function useSmilesExport({ mode, filters, scopeSlug }: UseSmilesExportOptions) {
  const [status, setStatus] = useState<SmilesExportStatus>("idle");
  const [loaded, setLoaded] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const { download } = useDownload();

  const cancel = useCallback(() => {
    const c = controllerRef.current;
    if (!c) return;
    setStatus("cancelling");
    c.abort();
  }, []);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const start = useCallback(async () => {
    // Synchronous guard: a rapid double-click can invoke start() twice before
    // the `status` state update from the first call has rendered, so we can't
    // rely on `status` alone to block re-entry.
    if (controllerRef.current) return;

    const controller = new AbortController();
    controllerRef.current = controller;
    const { signal } = controller;

    setStatus("loading");
    setError(null);
    setLoaded(0);
    setTotal(0);

    const apiFilters = filters ?? {};

    try {
      const molecules: ExportMolecule[] = [];
      const reactions: ExportReaction[] = [];
      let moleculesTotal = 0;
      let reactionsTotal = 0;

      const wantsMolecules = mode === "molecules" || mode === "combined";
      const wantsReactions = mode === "reactions" || mode === "combined";

      // First-page fetches (sequential) to learn totals and apply the cap
      // before pulling anything else.
      let combinedTotal = 0;

      if (wantsMolecules) {
        const { data: first } = await getAllMolecules(
          { ...apiFilters, limit: BATCH_SIZE, offset: 0 },
          signal,
        );
        if (signal.aborted) throw new Error("aborted");
        moleculesTotal = first.total ?? 0;
        combinedTotal += moleculesTotal;
        for (const m of first.molecules as RawMolecule[]) {
          molecules.push(shapeMolecule(m));
        }
      }

      if (wantsReactions) {
        const { data: first } = await getAllReactions(
          { ...apiFilters, limit: BATCH_SIZE, offset: 0 },
          signal,
        );
        if (signal.aborted) throw new Error("aborted");
        reactionsTotal = first.total ?? 0;
        combinedTotal += reactionsTotal;
        for (const r of first.reactions as RawReaction[]) {
          reactions.push(shapeReaction(r));
        }
      }

      if (combinedTotal > SMILES_EXPORT_ROW_CAP) {
        setError(
          `Too many rows to export (${combinedTotal.toLocaleString()} > ${SMILES_EXPORT_ROW_CAP.toLocaleString()}). Please narrow your filter.`,
        );
        setStatus("error");
        controllerRef.current = null;
        return;
      }

      setTotal(combinedTotal);
      setLoaded(molecules.length + reactions.length);

      // Molecules paging (sequential)
      if (wantsMolecules) {
        while (molecules.length < moleculesTotal) {
          if (signal.aborted) throw new Error("aborted");
          const { data } = await getAllMolecules(
            { ...apiFilters, limit: BATCH_SIZE, offset: molecules.length },
            signal,
          );
          if (signal.aborted) throw new Error("aborted");
          const batch = (data.molecules as RawMolecule[]) || [];
          for (const m of batch) molecules.push(shapeMolecule(m));
          setLoaded(molecules.length + reactions.length);
          if (batch.length < BATCH_SIZE) break;
        }
      }

      // Reactions paging (sequential, after molecules finish)
      if (wantsReactions) {
        while (reactions.length < reactionsTotal) {
          if (signal.aborted) throw new Error("aborted");
          const { data } = await getAllReactions(
            { ...apiFilters, limit: BATCH_SIZE, offset: reactions.length },
            signal,
          );
          if (signal.aborted) throw new Error("aborted");
          const batch = (data.reactions as RawReaction[]) || [];
          for (const r of batch) reactions.push(shapeReaction(r));
          setLoaded(molecules.length + reactions.length);
          if (batch.length < BATCH_SIZE) break;
        }
      }

      if (signal.aborted) throw new Error("aborted");

      const now = new Date();
      const exportedAt = now.toISOString();
      const scope = scopeSlug ?? "all";
      const date = utcDate(now);

      let payload: unknown;
      let filename: string;

      if (mode === "molecules") {
        payload = {
          exported_at: exportedAt,
          count: molecules.length,
          molecules,
        };
        filename = `molecules-${scope}-${date}.json`;
      } else if (mode === "reactions") {
        payload = {
          exported_at: exportedAt,
          count: reactions.length,
          reactions,
        };
        filename = `reactions-${scope}-${date}.json`;
      } else {
        payload = {
          exported_at: exportedAt,
          molecules: { count: molecules.length, items: molecules },
          reactions: { count: reactions.length, items: reactions },
        };
        filename = `smiles-export-${date}.json`;
      }

      download(JSON.stringify(payload, null, 2), filename);
      setStatus("idle");
      controllerRef.current = null;
    } catch (err) {
      controllerRef.current = null;
      if (isAbortError(err) || (err instanceof Error && err.message === "aborted")) {
        setStatus("idle");
        setLoaded(0);
        setTotal(0);
        return;
      }
      const message =
        err instanceof Error ? err.message : "Failed to export SMILES";
      setError(message);
      setStatus("error");
    }
  }, [mode, filters, scopeSlug, download]);

  return { start, cancel, status, loaded, total, error };
}
